import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTimesheets, invalidateMonth } from '@redux';
import { RootState } from '@redux';

/**
 * タイムシートデータのフェッチおよびreduxの値の更新を管理するフック
 * 
 * データのフェッチおよびreduxの値の更新が完了するまで待機し、Promiseを返す
 * dispatchにはawaitを使用できないためこの方法を取った
 * 
 * @example
 * ```tsx
 * const { refreshTimesheetData } = useRefreshTimesheet();
 * 
 * const handleDelete = async () => {
 *   try {
 *     await timesheetApi.deleteTimesheet(id);
 *     // データのフェッチおよびreduxの値の更新が完了するまで待機
 *     await refreshTimesheetData('2024-03');
 *     // フェッチおよびreduxの値の更新が完了後に画面遷移
 *     history.push('/timesheet');
 *   } catch (error) {
 *     // エラー処理
 *   }
 * };
 * ```
 */
export const useRefreshTimesheet = () => {
  const dispatch = useDispatch();
  // Reduxの状態からAPIでのタイムシートデータのフェッチ状態を取得
  const loadingTimesheets = useSelector((state: RootState) => state.timesheets.loadingTimesheets);
  // useRefを使用して、非同期処理中でも最新の状態を参照できるようにする（useStateではダメ）
  // 詳しくはRef PatternもしくはClosure Trapで検索されたし
  const loadingRef = useRef(loadingTimesheets);

  // ローディング状態が変更されたら、refを更新
  useEffect(() => {
    loadingRef.current = loadingTimesheets;
  }, [loadingTimesheets]);

  /**
   * タイムシートデータをフェッチおよびreduxの値を更新する
   * 
   * @param month - 対象の月（YYYY-MM形式）
   * @returns Promise<void> - データのフェッチおよびreduxの値の更新が完了したらresolveされる
   * 
   * 処理の流れ:
   * 1. 該当月のデータを無効化
   * 2. データを再フェッチ
   * 3. ローディング状態を監視し、完了を待機
   * 
   * @example
   * ```tsx
   * // 削除後のデータ更新
   * await refreshTimesheetData('2024-03');
   * 
   * // 更新後のデータ更新
   * await refreshTimesheetData(values.check_date.substring(0, 7));
   * ```
   */
  const refreshTimesheetData = useCallback((month: string) => {
    return new Promise<void>((resolve) => {
      dispatch(invalidateMonth(month));
      dispatch(fetchTimesheets({ month }));

      // データの更新が完了するのを監視
      const checkLoading = () => {
        if (!loadingRef.current) {
          resolve();
        } else {
          setTimeout(checkLoading, 100);
        }
      };
      checkLoading();
    });
  }, [dispatch]);

  return { refreshTimesheetData };
}; 